/// <reference lib="dom" />
/** @jsx h */
import { h } from "preact";
import { useEffect } from "preact/hooks";
import type { ParamDict } from "@utils/params";
import { HzToNormalized, NormalizedToHz, QToResonance, ResonanceToQ } from "@wbaudio/utils";
import type { ChartMode } from "../../core/mode.ts";
import { useChart, useLatest } from "../../core/useChart.ts";
import { AreaLayer } from "../../layers/areas.ts";
import { LinesLayer } from "../../layers/lines.ts";
import { type HandlePosition, HandlesLayer } from "../../layers/handles.ts";
import { FormatHz } from "../../utils/format.ts";
import { CreateFrequencyChart, FrequencyDisplay, QScale } from "./frequencyChart.ts";
import { ResponsePoints } from "./filterCurve.tsx";

export const MoogResonanceQ = { qMin: 0.5, qMax: 10.5 };

export interface MoogFilterCurveProps {
  params: ParamDict;
  cutoffParam: { min: number; max: number };
  resonanceParam: { min: number; max: number };
  onChange?: (key: string, value: unknown) => void;
}

function CreateMoogChart(
  container: HTMLElement,
  mode: ChartMode,
  onDrag: (id: string, position: HandlePosition) => void,
) {
  const fill = mode === "editor" ? AreaLayer() : null;
  const curve = LinesLayer();
  const handles = mode === "editor"
    ? HandlesLayer({ y: QScale(MoogResonanceQ.qMin, MoogResonanceQ.qMax), onDrag })
    : null;
  const chart = CreateFrequencyChart(container, {
    mode,
    layers: [...(fill ? [fill] : []), curve, ...(handles ? [handles] : [])],
  });
  return { chart, fill, curve, handles, destroy: chart.destroy };
}

export function MoogFilterCurve(props: MoogFilterCurveProps) {
  const mode: ChartMode = props.onChange ? "editor" : "inline";
  const latest = useLatest(props);
  const { ref, chart } = useChart(
    (container) =>
      CreateMoogChart(container, mode, (_, p) => {
        const { onChange, cutoffParam, resonanceParam } = latest.current;
        const { qMin, qMax } = MoogResonanceQ;
        onChange?.(
          "cutoff",
          parseFloat(
            HzToNormalized(p.x, FrequencyDisplay.fMax, cutoffParam.min, cutoffParam.max).toFixed(4),
          ),
        );
        onChange?.(
          "resonance",
          parseFloat(
            QToResonance(p.y, qMin, qMax, resonanceParam.min, resonanceParam.max).toFixed(2),
          ),
        );
      }),
    [mode],
  );

  const cutoff = props.params.cutoff.value as number;
  const resonance = props.params.resonance.value as number;

  useEffect(() => {
    if (!chart) return;
    const freqHz = NormalizedToHz(cutoff, FrequencyDisplay.fMin, FrequencyDisplay.fMax);
    const q = ResonanceToQ(
      resonance,
      props.resonanceParam.max,
      MoogResonanceQ.qMin,
      MoogResonanceQ.qMax,
    );
    const points = ResponsePoints("lowpass", freqHz, q);
    chart.fill?.set([{ id: "fill", points }]);
    chart.curve.set([{ id: "curve", points }]);
    chart.handles?.set([{ id: "cutoff", x: freqHz, y: q, radius: 5, label: FormatHz(freqHz) }]);
    chart.chart.render();
  }, [chart, cutoff, resonance, props.resonanceParam.max]);

  return <div ref={ref} />;
}
